import { extract, isNullish, nextItem, previousItem, useArrayKeyboardHandler, } from '@vpap_svelte/internal';
export function useHierarchicalKeyboard(options) {
    let data = $derived(extract(options.data));
    let handler = useArrayKeyboardHandler({ orientation: options.orientation ?? 'horizontal' });
    let root = $state(extract(options.data));
    // where within the siblings the node is
    // (this allows us to traverse throughout the siblings)
    let cursor = $state(undefined);
    let siblings = $derived(root?.parent?.children);
    return {
        get options() {
            return options;
        },
        get data() {
            return data;
        },
        get root() {
            return root;
        },
        get siblings() {
            return siblings;
        },
        initialize() {
            root = data;
        },
        reset() {
            root = undefined;
        },
        // goes to the next sibling
        nextSibling() {
            if (siblings === undefined)
                return;
            const n = nextItem(siblings, cursor ?? -1, {
                wrapEnd: options.wrap ?? true,
                skipIf: (node) => {
                    // adjust the default skip function to automatically skip the active (root) node
                    if (node === root)
                        return true;
                    if (options.skipIf !== undefined) {
                        return options.skipIf(node);
                    }
                    return false;
                },
            });
            // we only change the cursor if it isn't undefined so wrapping works correctly
            if (n !== undefined) {
                cursor = n;
            }
            if (!isNullish(cursor)) {
                root = siblings[cursor];
            }
        },
        previousSibling() {
            if (siblings === undefined)
                return;
            const p = previousItem(siblings, cursor ?? 0, {
                wrapStart: options.wrap ?? true,
                skipIf: (node) => {
                    if (node === root)
                        return true;
                    if (options.skipIf !== undefined)
                        return options.skipIf(node);
                    return false;
                },
            });
            if (p !== undefined) {
                cursor = p;
            }
            if (!isNullish(cursor)) {
                root = siblings[cursor];
            }
        },
        firstChild() {
            const child = root?.children?.at(0);
            if (isNullish(child))
                return;
            cursor = undefined;
            root = child;
        },
        parent() {
            const parent = root?.parent;
            if (isNullish(parent))
                return;
            cursor = undefined;
            root = parent;
        },
        get selection() {
            return root;
        },
        get attributes() {
            return {
                role: 'application',
                tabindex: 0,
                onfocus: () => {
                    this.initialize();
                },
                onkeydown: (e) => {
                    const isNext = handler.isNextKey(e);
                    const isPrevious = handler.isPreviousKey(e);
                    if (!isNext || isPrevious || e.code === 'Enter' || e.code === 'Backspace') {
                        return;
                    }
                    if (e.code === 'Enter') {
                        this.firstChild();
                    }
                    else if (e.code === 'Backspace') {
                        this.parent();
                    }
                    else if (isNext) {
                        this.nextSibling();
                    }
                    else if (isPrevious) {
                        this.previousSibling();
                    }
                },
                onblur: () => {
                    this.reset();
                },
            };
        },
    };
}
